/**
 * Standard reduction potentials for the Nernst and cell-potential calculators.
 *
 * Source: OpenStax Chemistry 2e, Appendix L, "Standard Electrode (Half-Cell)
 * Potentials" (Table L1), at 25 °C, values as printed (volts versus the standard
 * hydrogen electrode). This is a hand-picked subset of that table's couples; the table
 * writes the hydrogen ion as H₃O⁺ and this file writes H⁺. Other compilations differ in
 * the third decimal for some couples (for example Cu²⁺/Cu), so a result is only as exact
 * as the table behind it.
 *
 * Each couple is stored as the reduction half-reaction, without the electrons, whose
 * count is `n`: `left` are the reactants, `right` the products.
 */

export type Species = readonly [coefficient: number, formula: string];

export interface HalfCell {
  id: string;
  /** The couple as chemists write it: oxidized | reduced. */
  couple: string;
  n: number;
  /** E° in volts. */
  e0: number;
  left: readonly Species[];
  right: readonly Species[];
}

export const HALF_CELLS: readonly HalfCell[] = [
  {
    id: "li",
    couple: "Li⁺ | Li",
    n: 1,
    e0: -3.04,
    left: [[1, "Li⁺"]],
    right: [[1, "Li"]],
  },
  {
    id: "k",
    couple: "K⁺ | K",
    n: 1,
    e0: -2.931,
    left: [[1, "K⁺"]],
    right: [[1, "K"]],
  },
  {
    id: "ca",
    couple: "Ca²⁺ | Ca",
    n: 2,
    e0: -2.868,
    left: [[1, "Ca²⁺"]],
    right: [[1, "Ca"]],
  },
  {
    id: "na",
    couple: "Na⁺ | Na",
    n: 1,
    e0: -2.71,
    left: [[1, "Na⁺"]],
    right: [[1, "Na"]],
  },
  {
    id: "mg",
    couple: "Mg²⁺ | Mg",
    n: 2,
    e0: -2.372,
    left: [[1, "Mg²⁺"]],
    right: [[1, "Mg"]],
  },
  {
    id: "al",
    couple: "Al³⁺ | Al",
    n: 3,
    e0: -1.662,
    left: [[1, "Al³⁺"]],
    right: [[1, "Al"]],
  },
  {
    id: "mn",
    couple: "Mn²⁺ | Mn",
    n: 2,
    e0: -1.185,
    left: [[1, "Mn²⁺"]],
    right: [[1, "Mn"]],
  },
  {
    id: "zn",
    couple: "Zn²⁺ | Zn",
    n: 2,
    e0: -0.7618,
    left: [[1, "Zn²⁺"]],
    right: [[1, "Zn"]],
  },
  {
    id: "cr",
    couple: "Cr³⁺ | Cr",
    n: 3,
    e0: -0.744,
    left: [[1, "Cr³⁺"]],
    right: [[1, "Cr"]],
  },
  {
    id: "fe",
    couple: "Fe²⁺ | Fe",
    n: 2,
    e0: -0.447,
    left: [[1, "Fe²⁺"]],
    right: [[1, "Fe"]],
  },
  {
    id: "cd",
    couple: "Cd²⁺ | Cd",
    n: 2,
    e0: -0.403,
    left: [[1, "Cd²⁺"]],
    right: [[1, "Cd"]],
  },
  {
    id: "co",
    couple: "Co²⁺ | Co",
    n: 2,
    e0: -0.28,
    left: [[1, "Co²⁺"]],
    right: [[1, "Co"]],
  },
  {
    id: "ni",
    couple: "Ni²⁺ | Ni",
    n: 2,
    e0: -0.257,
    left: [[1, "Ni²⁺"]],
    right: [[1, "Ni"]],
  },
  {
    id: "sn",
    couple: "Sn²⁺ | Sn",
    n: 2,
    e0: -0.1375,
    left: [[1, "Sn²⁺"]],
    right: [[1, "Sn"]],
  },
  {
    id: "pb",
    couple: "Pb²⁺ | Pb",
    n: 2,
    e0: -0.1262,
    left: [[1, "Pb²⁺"]],
    right: [[1, "Pb"]],
  },
  {
    id: "h",
    couple: "H⁺ | H₂ (SHE)",
    n: 2,
    e0: 0,
    left: [[2, "H⁺"]],
    right: [[1, "H₂"]],
  },
  {
    id: "sn4",
    couple: "Sn⁴⁺ | Sn²⁺",
    n: 2,
    e0: 0.151,
    left: [[1, "Sn⁴⁺"]],
    right: [[1, "Sn²⁺"]],
  },
  {
    id: "cu2cu1",
    couple: "Cu²⁺ | Cu⁺",
    n: 1,
    e0: 0.153,
    left: [[1, "Cu²⁺"]],
    right: [[1, "Cu⁺"]],
  },
  {
    id: "agcl",
    couple: "AgCl | Ag, Cl⁻",
    n: 1,
    e0: 0.222_33,
    left: [[1, "AgCl"]],
    right: [
      [1, "Ag"],
      [1, "Cl⁻"],
    ],
  },
  {
    id: "sce",
    couple: "Hg₂Cl₂ | Hg, Cl⁻",
    n: 2,
    e0: 0.268_08,
    left: [[1, "Hg₂Cl₂"]],
    right: [
      [2, "Hg"],
      [2, "Cl⁻"],
    ],
  },
  {
    id: "cu",
    couple: "Cu²⁺ | Cu",
    n: 2,
    e0: 0.337,
    left: [[1, "Cu²⁺"]],
    right: [[1, "Cu"]],
  },
  {
    id: "o2oh",
    couple: "O₂ | OH⁻ (basic)",
    n: 4,
    e0: 0.401,
    left: [
      [1, "O₂"],
      [2, "H₂O"],
    ],
    right: [[4, "OH⁻"]],
  },
  {
    id: "cu1",
    couple: "Cu⁺ | Cu",
    n: 1,
    e0: 0.521,
    left: [[1, "Cu⁺"]],
    right: [[1, "Cu"]],
  },
  {
    id: "i2",
    couple: "I₂ | I⁻",
    n: 2,
    e0: 0.5355,
    left: [[1, "I₂"]],
    right: [[2, "I⁻"]],
  },
  {
    id: "fe3",
    couple: "Fe³⁺ | Fe²⁺",
    n: 1,
    e0: 0.771,
    left: [[1, "Fe³⁺"]],
    right: [[1, "Fe²⁺"]],
  },
  {
    id: "hg2",
    couple: "Hg₂²⁺ | Hg",
    n: 2,
    e0: 0.7973,
    left: [[1, "Hg₂²⁺"]],
    right: [[2, "Hg"]],
  },
  {
    id: "ag",
    couple: "Ag⁺ | Ag",
    n: 1,
    e0: 0.7996,
    left: [[1, "Ag⁺"]],
    right: [[1, "Ag"]],
  },
  {
    id: "br2",
    couple: "Br₂ | Br⁻",
    n: 2,
    e0: 1.0873,
    left: [[1, "Br₂"]],
    right: [[2, "Br⁻"]],
  },
  {
    id: "o2h2o",
    couple: "O₂ | H₂O (acidic)",
    n: 4,
    e0: 1.229,
    left: [
      [1, "O₂"],
      [4, "H⁺"],
    ],
    right: [[2, "H₂O"]],
  },
  {
    id: "dichromate",
    couple: "Cr₂O₇²⁻ | Cr³⁺",
    n: 6,
    e0: 1.232,
    left: [
      [1, "Cr₂O₇²⁻"],
      [14, "H⁺"],
    ],
    right: [
      [2, "Cr³⁺"],
      [7, "H₂O"],
    ],
  },
  {
    id: "cl2",
    couple: "Cl₂ | Cl⁻",
    n: 2,
    e0: 1.358_27,
    left: [[1, "Cl₂"]],
    right: [[2, "Cl⁻"]],
  },
  {
    id: "au",
    couple: "Au³⁺ | Au",
    n: 3,
    e0: 1.498,
    left: [[1, "Au³⁺"]],
    right: [[1, "Au"]],
  },
  {
    id: "mno4",
    couple: "MnO₄⁻ | Mn²⁺",
    n: 5,
    e0: 1.507,
    left: [
      [1, "MnO₄⁻"],
      [8, "H⁺"],
    ],
    right: [
      [1, "Mn²⁺"],
      [4, "H₂O"],
    ],
  },
  {
    id: "ce",
    couple: "Ce⁴⁺ | Ce³⁺",
    n: 1,
    e0: 1.61,
    left: [[1, "Ce⁴⁺"]],
    right: [[1, "Ce³⁺"]],
  },
  {
    id: "pbo2",
    couple: "PbO₂ | PbSO₄",
    n: 2,
    e0: 1.69,
    left: [
      [1, "PbO₂"],
      [1, "SO₄²⁻"],
      [4, "H⁺"],
    ],
    right: [
      [1, "PbSO₄"],
      [2, "H₂O"],
    ],
  },
  {
    id: "h2o2",
    couple: "H₂O₂ | H₂O",
    n: 2,
    e0: 1.776,
    left: [
      [1, "H₂O₂"],
      [2, "H⁺"],
    ],
    right: [[2, "H₂O"]],
  },
  {
    id: "f2",
    couple: "F₂ | F⁻",
    n: 2,
    e0: 2.866,
    left: [[1, "F₂"]],
    right: [[2, "F⁻"]],
  },
];

export function findHalfCell(id: string): HalfCell | undefined {
  return HALF_CELLS.find((cell) => cell.id === id);
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

export function lcm(a: number, b: number): number {
  return (a * b) / gcd(a, b);
}

export interface CellResult {
  /** E°cathode − E°anode, volts. */
  e0: number;
  /** Electrons transferred in the balanced equation. */
  n: number;
  cathodeMultiplier: number;
  anodeMultiplier: number;
  reactants: Species[];
  products: Species[];
}

/**
 * Combines a reduction (cathode) with the reverse of another (anode): scales both to the
 * same number of electrons, adds them, and cancels species that appear on both sides.
 */
export function combineHalfCells(
  cathodeId: string,
  anodeId: string
): CellResult | null {
  const cathode = findHalfCell(cathodeId);
  const anode = findHalfCell(anodeId);
  if (!(cathode && anode) || cathode.id === anode.id) {
    return null;
  }
  const n = lcm(cathode.n, anode.n);
  const a = n / cathode.n;
  const b = n / anode.n;
  const net = new Map<string, number>();
  const add = (list: readonly Species[], factor: number) => {
    for (const [coefficient, formula] of list) {
      net.set(formula, (net.get(formula) ?? 0) + coefficient * factor);
    }
  };
  add(cathode.left, a);
  add(cathode.right, -a);
  add(anode.right, b);
  add(anode.left, -b);
  const reactants: Species[] = [];
  const products: Species[] = [];
  for (const [formula, amount] of net) {
    if (amount > 0) {
      reactants.push([amount, formula]);
    } else if (amount < 0) {
      products.push([-amount, formula]);
    }
  }
  return {
    e0: cathode.e0 - anode.e0,
    n,
    cathodeMultiplier: a,
    anodeMultiplier: b,
    reactants,
    products,
  };
}

/** "2Ag⁺ + Zn" */
export function speciesText(list: readonly Species[]): string {
  return list
    .map(
      ([coefficient, formula]) =>
        `${coefficient === 1 ? "" : coefficient}${formula}`
    )
    .join(" + ");
}
